import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTrial } from '../contexts/TrialContext';
import { useTranslation } from '../contexts/LanguageContext';
import apiClient from '../utils/apiClient';
import RankBadge from './RankBadge';
import PurchaseHistory from './PurchaseHistory';
import SubscriptionManager from './SubscriptionManager';
import SubscriptionHealthWidget from './SubscriptionHealthWidget';
import { TitleBadgeInline, useKingdomTitles } from './KingdomTitle';
import { 
  AVATAR_CATALOG_V2,
  getCatalogWithStatusV2,
  is3DPackUnlocked,
  avatarCatalog
} from '../data/avatarCatalogV2';
import '../styles/ProfilePage.css';
import logger from '../utils/logger';

// Helper function to get avatar image path from ID
const getAvatarImagePath = (avatarIdOrPath) => {
  if (!avatarIdOrPath) return null;
  
  // If it's already a path or data URL, return as-is
  if (avatarIdOrPath.startsWith('/avatars') || 
      avatarIdOrPath.startsWith('data:image') || 
      avatarIdOrPath.startsWith('http')) {
    return avatarIdOrPath;
  }
  
  // If it's an emoji character, return null (will be rendered as text)
  if (avatarIdOrPath.length < 10 && !avatarIdOrPath.includes('.')) {
    return null;
  }
  
  // Look up the avatar in catalog by ID
  const avatar = avatarCatalog.find(av => av.id === avatarIdOrPath);
  return avatar ? avatar.imagePath : null;
};

// Helper function to get avatar emoji from ID
const getAvatarEmoji = (avatarIdOrPath) => {
  if (!avatarIdOrPath) return '👤';
  
  // If it's already an emoji, return it
  if (avatarIdOrPath.length < 10 && !avatarIdOrPath.includes('.')) {
    return avatarIdOrPath;
  }
  
  // Look up the avatar in catalog by ID
  const avatar = avatarCatalog.find(av => av.id === avatarIdOrPath);
  if (avatar) {
    // Generate emoji based on piece and color
    const emojis = {
      pawn: avatar.color === 'white' ? '♙' : '♟',
      knight: avatar.color === 'white' ? '♘' : '♞',
      bishop: avatar.color === 'white' ? '♗' : '♝',
      rook: avatar.color === 'white' ? '♖' : '♜',
      queen: avatar.color === 'white' ? '♕' : '♛',
      king: avatar.color === 'white' ? '♔' : '♚',
      none: '🎨'
    };
    return emojis[avatar.piece] || '♟';
  }
  
  return '👤';
};

const ProfilePage = ({ onBack, onUpgrade, onNavigate }) => {
  const { user, isAuthenticated, isGuest, logout, loading } = useAuth();
  const { isTrialActive, trialTimeRemaining, hasActiveAccess } = useTrial();
  const { t } = useTranslation();
  const [stats, setStats] = useState({ wins: 0, losses: 0, resignations: 0 });
  const [rank, setRank] = useState(null);
  const [showAvatarSelector, setShowAvatarSelector] = useState(false);
  const [avatarTimestamp, setAvatarTimestamp] = useState(Date.now());
  const [selectedPackId, setSelectedPackId] = useState('classic-flat');
  const [catalogV2, setCatalogV2] = useState(null);
  
  // Terms/Legal Agreements state
  const [termsHistory, setTermsHistory] = useState([]);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [selectedTermsText, setSelectedTermsText] = useState(null);
  const [selectedTermsVersion, setSelectedTermsVersion] = useState(null);
  const [loadingTerms, setLoadingTerms] = useState(false);
  const [showPurchaseHistory, setShowPurchaseHistory] = useState(false);
  const [tournamentStats, setTournamentStats] = useState(null);
  const [dukeShowcase, setDukeShowcase] = useState({ owned_duke_skins: [], showcase_skins: [], showcase_title: '' });
  const [showcaseEditing, setShowcaseEditing] = useState(false);
  const [showcaseTitle, setShowcaseTitle] = useState('');
  const [showcaseSelected, setShowcaseSelected] = useState([]);
  const [giftInbox, setGiftInbox] = useState([]);
  const [showGiftInbox, setShowGiftInbox] = useState(false);
  const [trialNotificationsEnabled, setTrialNotificationsEnabled] = useState(() => {
    const saved = localStorage.getItem('trialNotificationsEnabled');
    return saved !== null ? JSON.parse(saved) : true;
  });

  useEffect(() => {
    if (user && !isGuest) {
      fetchStats();
      fetchTermsHistory();
      fetchTournamentStats();
      fetchDukeShowcase();
      fetchGiftInbox();
      const catalog = getCatalogWithStatusV2(user);
      setCatalogV2(catalog);
    }
  }, [user, isGuest]);
  
  const fetchTermsHistory = async () => {
    try {
      const response = await apiClient.get('/terms/history');
      if (response.data.success) {
        setTermsHistory(response.data.history);
      }
    } catch (error) {
      logger.error('Error fetching terms history:', error);
    }
  };
  
  const viewAcceptedTerms = async (version) => {
    setLoadingTerms(true);
    try {
      const response = await apiClient.get(`/terms/accepted/${version}`);
      if (response.data.success) {
        setSelectedTermsText(response.data.terms_text);
        setSelectedTermsVersion(response.data.terms_version);
        setShowTermsModal(true);
      }
    } catch (error) {
      logger.error('Error fetching accepted terms:', error);
      alert('Failed to load terms. Please try again.');
    } finally {
      setLoadingTerms(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await apiClient.get('/profile/stats');
      setStats(response.data.stats);
      setRank(response.data.rank);
    } catch (error) {
      logger.error('Error fetching stats:', error);
      // NOTE: Intentionally do NOT auto-logout on 401 here. On PWA cold-start
      // the in-memory `token` is null until /auth/me restores it from the
      // HttpOnly cookie — a transient 401 during that window is normal, not
      // a real auth failure. Auth expiry is handled centrally in AuthContext.
    }
  };

  const fetchTournamentStats = async () => {
    try {
      if (!user?.user_id) return;
      const response = await apiClient.get(`/tournaments/stats/${user.user_id}`);
      if (response.data.success) {
        setTournamentStats(response.data);
      }
    } catch (error) {
      logger.error('Error fetching tournament stats:', error);
    }
  };

  const fetchDukeShowcase = async () => {
    try {
      if (!user?.user_id) return;
      const response = await apiClient.get(`/duke-showcase/${user.user_id}`);
      if (response.data) {
        setDukeShowcase(response.data);
        setShowcaseSelected(response.data.showcase_skins || []);
        setShowcaseTitle(response.data.showcase_title || '');
      }
    } catch (error) {
      logger.error('Error fetching duke showcase:', error);
    }
  };

  const saveDukeShowcase = async () => {
    try {
      await apiClient.post('/duke-showcase/update', {
        featured_skins: showcaseSelected,
        title: showcaseTitle
      });
      setShowcaseEditing(false);
      fetchDukeShowcase();
    } catch (error) {
      logger.error('Error saving duke showcase:', error);
    }
  };

  const toggleShowcaseSkin = (skinId) => {
    setShowcaseSelected(prev => {
      if (prev.includes(skinId)) return prev.filter(s => s !== skinId);
      if (prev.length >= 6) return prev;
      return [...prev, skinId];
    });
  };

  const getDukeImageUrl = (skinId, variant) => {
    return `/duke-skins/${skinId}/${variant}.png`;
  };

  const fetchGiftInbox = async () => {
    try {
      const response = await apiClient.get('/gifts/inbox');
      if (response.data) {
        setGiftInbox(response.data.gifts || []);
      }
    } catch (error) {
      logger.error('Error fetching gift inbox:', error);
    }
  };




  const handleDeleteAccount = async () => {
    if (!window.confirm('Are you sure you want to delete your account? This action cannot be undone.')) {
      return;
    }
    
    if (!window.confirm('FINAL WARNING: All your data, including game history and stats, will be permanently deleted. Continue?')) {
      return;
    }
    
    try {
      const response = await apiClient.delete('/auth/delete-account');
      
      if (response.data.success) {
        alert('Account deleted successfully.');
        logout();
        // Navigate to cover page
        if (onNavigate) {
          onNavigate('cover');
        }
      }
    } catch (error) {
      logger.error('Error deleting account:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        alert('Session expired. Please log in again.');
        logout();
        if (onNavigate) onNavigate('cover');
      } else {
        alert('Failed to delete account. Please try again.');
      }
    }
  };

  const handleAvatarUpdate = async (avatarData) => {
    try {
      const response = await apiClient.post('/profile/avatar', { avatar: avatarData }, { headers: { 'Content-Type': 'application/json' } });
      
      if (response.data.success) {
        // Update timestamp to force image refresh
        setAvatarTimestamp(Date.now());
        
        // Trigger avatar update event for AuthContext
        window.dispatchEvent(new CustomEvent('avatarUpdated', {
          detail: { avatar: avatarData }
        }));
        
        // Force re-fetch user data to update avatar
        const userResponse = await apiClient.get('/auth/me');
        setShowAvatarSelector(false);
        alert('✅ Profile picture updated!');
      }
    } catch (error) {
      logger.error('❌ Error updating avatar:', error);
      if (error.response?.data) {
        logger.error('Server error:', error.response.data);
      }
      alert('Failed to update avatar. Please try again.');
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file.');
      return;
    }

    // Validate file size (max 5MB for original)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image size must be less than 5MB.');
      return;
    }

    try {
      // Create image element
      const img = new Image();
      const reader = new FileReader();

      reader.onload = (e) => {
        img.src = e.target.result;
      };

      img.onload = async () => {
        // Create canvas for resizing
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        // Set target size (256x256 for profile pictures)
        const MAX_SIZE = 256;
        let width = img.width;
        let height = img.height;

        // Calculate new dimensions (maintain aspect ratio)
        if (width > height) {
          if (width > MAX_SIZE) {
            height = Math.round((height * MAX_SIZE) / width);
            width = MAX_SIZE;
          }
        } else {
          if (height > MAX_SIZE) {
            width = Math.round((width * MAX_SIZE) / height);
            height = MAX_SIZE;
          }
        }

        // Set canvas size
        canvas.width = width;
        canvas.height = height;

        // Draw resized image
        ctx.drawImage(img, 0, 0, width, height);

        // Convert to base64 (JPEG with 85% quality for smaller size)
        const resizedBase64 = canvas.toDataURL('image/jpeg', 0.85);
        // Update avatar with resized image
        await handleAvatarUpdate(resizedBase64);
      };

      reader.readAsDataURL(file);
    } catch (error) {
      logger.error('Error uploading image:', error);
      alert('Failed to upload image. Please try again.');
    }
  };

  const features = [
    {
      id: 'standard_chess',
      name: 'Standard Chess',
      icon: '♟️',
      description: 'Classic chess with traditional rules',
      unlocked: true,
      category: 'game_mode',
      price: 'FREE',
      requiresPremium: false
    },
    {
      id: 'fight_to_be_king',
      name: 'Fight to be King',
      icon: '⚔️',
      description: 'Strategic battle to promote Duke to King',
      unlocked: true,
      category: 'game_mode',
      price: 'FREE',
      requiresPremium: false
    },
    {
      id: 'kings_court_2dukes',
      name: 'Chess Kingdom (Two Dukes)',
      icon: '👑',
      description: 'Play with two Dukes and unique King capture rules',
      unlocked: hasActiveAccess(),
      category: 'game_mode',
      price: '$4.99',
      requiresPremium: true,
      unlockCondition: 'Win 50 games vs AI (Normal)'
    },
    {
      id: 'instant_king',
      name: 'Instant King',
      icon: '⚡',
      description: 'Duke becomes King immediately when original King is captured',
      unlocked: hasActiveAccess(),
      category: 'game_mode',
      price: '$3.99',
      requiresPremium: true,
      unlockCondition: 'Win 30 games vs AI (Normal)'
    },
    {
      id: 'players_choice',
      name: "Player's Choice",
      icon: '🎯',
      description: 'Choose your embodiment strategy before the game',
      unlocked: hasActiveAccess(),
      category: 'game_mode',
      price: '$3.99',
      requiresPremium: true,
      unlockCondition: 'Win 30 games vs AI (Normal)'
    },
    {
      id: 'pawns_wild',
      name: "King's Court: Pawn's Wild",
      icon: '🛡️',
      description: 'Pawns can promote to Dukes',
      unlocked: hasActiveAccess(),
      category: 'game_mode',
      price: '$2.99',
      requiresPremium: true,
      unlockCondition: 'Win 20 games vs AI (Normal)'
    },
    {
      id: 'handicap_mode',
      name: 'Handicap Mode',
      icon: '⚖️',
      description: 'Standard Chess vs Royal Chess Kingdom',
      unlocked: hasActiveAccess(),
      category: 'game_mode',
      price: '$2.99',
      requiresPremium: true,
      unlockCondition: 'Win 20 games vs AI (Normal)'
    },
    {
      id: 'multiplayer',
      name: 'Multiplayer',
      icon: '🌐',
      description: 'Play against other players online',
      unlocked: true,
      category: 'feature',
      price: 'FREE',
      requiresPremium: false
    },
    {
      id: 'ai_opponent',
      name: 'AI Opponent',
      icon: '🤖',
      description: 'Play against computer with adjustable difficulty',
      unlocked: true,
      category: 'feature',
      price: 'FREE',
      requiresPremium: false
    },
    {
      id: 'chat',
      name: 'In-Game Chat',
      icon: '💬',
      description: 'Chat with opponents during games',
      unlocked: hasActiveAccess(),
      category: 'feature',
      price: '$0.99',
      requiresPremium: true,
      unlockCondition: 'Win 10 games vs AI (Normal)'
    },
    {
      id: 'customization',
      name: 'Board & Piece Customization',
      icon: '🎨',
      description: 'Customize colors and appearance',
      unlocked: hasActiveAccess(),
      category: 'feature',
      price: '$1.99',
      requiresPremium: true,
      unlockCondition: 'Win 15 games vs AI (Normal)'
    },
    {
      id: 'rank_system',
      name: 'Rank System',
      icon: '🏅',
      description: 'Track your progress and earn ranks',
      unlocked: true,
      category: 'feature',
      price: 'FREE',
      requiresPremium: false
    }
  ];

  const gameModes = features.filter(f => f.category === 'game_mode');
  const otherFeatures = features.filter(f => f.category === 'feature');

  return (
    <div className="profile-page">
      <div className="profile-container">
        <button className="back-btn" onClick={onBack}>
          ← Back to Lobby
        </button>

        {/* User Profile Section */}
        <div className="profile-header-section">
          <div className="profile-avatar-large" onClick={() => setShowAvatarSelector(true)}>
            {user?.avatar ? (
              (() => {
                const imagePath = getAvatarImagePath(user.avatar);
                const emoji = getAvatarEmoji(user.avatar);
                
                // Check if it's a data URL (base64) or regular URL
                const isDataUrl = imagePath && imagePath.startsWith('data:');
                const imageSrc = imagePath ? (
                  isDataUrl ? imagePath : `${imagePath}?t=${avatarTimestamp}`
                ) : null;
                
                return imageSrc ? (
                  <img 
                    src={imageSrc} 
                    alt="Profile" 
                    className="avatar-image"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      imageRendering: 'auto',
                      borderRadius: '50%'
                    }}
                    onError={(e) => {
                      logger.error('Failed to load avatar image');
                      e.target.style.display = 'none';
                      e.target.parentElement.innerHTML = '<span style="font-size: 48px;">👤</span><div class="avatar-edit-overlay"><span>✏️ Edit</span></div>';
                    }}
                  />
                ) : (
                  <span style={{ fontSize: '48px' }}>{emoji}</span>
                );
              })()
            ) : (
              <span style={{ fontSize: '48px' }}>👤</span>
            )}
            <div className="avatar-edit-overlay">
              <span>✏️ Edit</span>
            </div>
          </div>
          <div className="profile-info-section">
            <h1 className="profile-username">{user?.name || 'Player'}</h1>
            {user?.user_id && <TitleBadgeInline userId={user.user_id} size="lg" />}
            <p className="profile-email">{user?.email || 'Guest'}</p>
            {rank && (
              <div className="profile-rank-display">
                <RankBadge rank={rank} stats={stats} size="large" />
              </div>
            )}
          </div>
        </div>

        {/* Avatar Selector Modal */}
        {showAvatarSelector && (
          <div className="avatar-selector-overlay" onClick={() => setShowAvatarSelector(false)}>
            <div className="avatar-selector-modal upload-only" onClick={(e) => e.stopPropagation()}>
              <h3>Upload Your Profile Picture</h3>
              <p className="upload-instructions">
                Choose a photo from your computer or smartphone. It will be automatically resized to fit.
              </p>
              
              {/* Upload Custom Image */}
              <div className="upload-avatar-section-large">
                <label htmlFor="avatar-upload" className="upload-avatar-btn-large">
                  <div className="upload-icon">📤</div>
                  <div className="upload-text">Choose Photo</div>
                  <div className="upload-subtext">From Computer or Phone</div>
                </label>
                <input
                  id="avatar-upload"
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handleImageUpload}
                  style={{ display: 'none' }}
                />
              </div>
              
              <div className="upload-info">
                <p>✓ Supports: JPG, PNG, GIF</p>
                <p>✓ Max size: 5MB</p>
                <p>✓ Auto-resized to 256x256px</p>
              </div>
              
              <button 
                className="cancel-btn" 
                onClick={() => setShowAvatarSelector(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Stats Section */}
        <div className="stats-section">
          <h2>Statistics</h2>
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-icon">🏆</div>
              <div className="stat-value">{stats.wins}</div>
              <div className="stat-label">Wins</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">💔</div>
              <div className="stat-value">{stats.losses}</div>
              <div className="stat-label">Losses</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">🏳️</div>
              <div className="stat-value">{stats.resignations}</div>
              <div className="stat-label">Resignations</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">📊</div>
              <div className="stat-value">
                {stats.wins + stats.losses + stats.resignations > 0
                  ? Math.round((stats.wins / (stats.wins + stats.losses + stats.resignations)) * 100)
                  : 0}%
              </div>
              <div className="stat-label">Win Rate</div>
            </div>
          </div>
        </div>

        {/* Tournament Stats */}
        {!isGuest && tournamentStats && (
          <div className="tournament-stats-section" data-testid="tournament-stats-section">
            <h2><i className="fa-solid fa-trophy" style={{ marginRight: 8, color: '#c9a227' }}></i> Tournament Record</h2>
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-icon">🏆</div>
                <div className="stat-value">{tournamentStats.tournament_wins}</div>
                <div className="stat-label">Tournament Wins</div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">🎮</div>
                <div className="stat-value">{tournamentStats.tournaments_participated}</div>
                <div className="stat-label">Participated</div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">🎯</div>
                <div className="stat-value">{tournamentStats.win_rate}%</div>
                <div className="stat-label">Win Rate</div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">📋</div>
                <div className="stat-value">{tournamentStats.tournaments_hosted}</div>
                <div className="stat-label">Hosted</div>
              </div>
            </div>
            {tournamentStats.recent_results && tournamentStats.recent_results.length > 0 && (
              <div className="recent-tournament-results" data-testid="recent-tournament-results">
                <h3>Recent Results</h3>
                {tournamentStats.recent_results.map((r) => (
                  <div key={`result-${r.name}-${r.completed_at}`} className="recent-result-row">
                    <span className="result-placement">
                      {r.placement === 'champion' ? '🏆' : r.placement === 'runner_up' ? '🥈' : r.placement === 'semi_finalist' ? '🥉' : '🎮'}
                    </span>
                    <span className="result-name">{r.name}</span>
                    <span className="result-variant">{r.variant?.replace(/_/g, ' ')}</span>
                    <span className="result-date">{r.completed_at ? new Date(r.completed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Trial Status */}
        {isTrialActive && (
          <div className="trial-status-section">
            <h3>🎁 Free Trial Active</h3>
            <p>Time remaining: {trialTimeRemaining}</p>
            <p>All premium features unlocked during trial!</p>
          </div>
        )}

        {/* Subscription Management */}
        {!isGuest && user && (
          <>
            <SubscriptionHealthWidget />
            <SubscriptionManager onUpgrade={onUpgrade} />
          </>
        )}

        {/* Game Modes Section */}
        <div className="features-section">
          <h2>Game Modes</h2>
          <div className="features-grid">
            {gameModes.map(feature => (
              <div 
                key={feature.id} 
                className={`feature-card ${feature.unlocked ? 'unlocked' : 'locked'}`}
              >
                <div className="feature-icon-large">{feature.icon}</div>
                <h3 className="feature-name">{feature.name}</h3>
                <p className="feature-description">{feature.description}</p>
                
                {feature.unlocked ? (
                  <div className="feature-status">
                    <span className="status-unlocked">✓ Unlocked</span>
                  </div>
                ) : (
                  <div className="feature-locked-info">
                    <div className="feature-price">{feature.price}</div>
                    {feature.unlockCondition && (
                      <div className="unlock-condition">
                        OR {feature.unlockCondition}
                      </div>
                    )}
                    <button className="unlock-btn" onClick={onUpgrade}>
                      Unlock Now
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Other Features Section */}
        <div className="features-section">
          <h2>Features</h2>
          <div className="features-grid">
            {otherFeatures.map(feature => (
              <div 
                key={feature.id} 
                className={`feature-card ${feature.unlocked ? 'unlocked' : 'locked'}`}
              >
                <div className="feature-icon-large">{feature.icon}</div>
                <h3 className="feature-name">{feature.name}</h3>
                <p className="feature-description">{feature.description}</p>
                
                {feature.unlocked ? (
                  <div className="feature-status">
                    <span className="status-unlocked">✓ Unlocked</span>
                  </div>
                ) : (
                  <div className="feature-locked-info">
                    <div className="feature-price">{feature.price}</div>
                    {feature.unlockCondition && (
                      <div className="unlock-condition">
                        OR {feature.unlockCondition}
                      </div>
                    )}
                    <button className="unlock-btn" onClick={onUpgrade}>
                      Unlock Now
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Upgrade Section */}
        {!hasActiveAccess() && (
          <div className="upgrade-section">
            <h2>🌟 Unlock All Features</h2>
            <p>Get access to all game modes, customization, chat, and more!</p>
            <button className="upgrade-btn" onClick={onUpgrade}>
              Upgrade to Premium
            </button>
          </div>
        )}


        {/* Duke Showcase */}
        {!isGuest && user && (
          <div className="duke-showcase-section" data-testid="duke-showcase-section">
            <div className="duke-showcase-header">
              <h2><i className="fa-solid fa-shield-halved" style={{ marginRight: 8 }}></i> Duke Showcase</h2>
              {!showcaseEditing ? (
                <button className="showcase-edit-btn" onClick={() => setShowcaseEditing(true)} data-testid="showcase-edit-btn">
                  <i className="fa-solid fa-pen"></i> Edit
                </button>
              ) : (
                <div className="showcase-edit-actions">
                  <button className="showcase-save-btn" onClick={saveDukeShowcase} data-testid="showcase-save-btn">Save</button>
                  <button className="showcase-cancel-btn" onClick={() => { setShowcaseEditing(false); setShowcaseSelected(dukeShowcase.showcase_skins || []); setShowcaseTitle(dukeShowcase.showcase_title || ''); }}>Cancel</button>
                </div>
              )}
            </div>
            {showcaseEditing && (
              <div className="showcase-title-edit">
                <input
                  type="text"
                  className="showcase-title-input"
                  placeholder="Showcase title (e.g., My Collection)"
                  value={showcaseTitle}
                  onChange={(e) => setShowcaseTitle(e.target.value)}
                  maxLength={50}
                  data-testid="showcase-title-input"
                />
              </div>
            )}
            {dukeShowcase.showcase_title && !showcaseEditing && (
              <p className="showcase-title-display">{dukeShowcase.showcase_title}</p>
            )}
            {!showcaseEditing && (dukeShowcase.showcase_skins?.length > 0 ? (
              <div className="showcase-grid" data-testid="showcase-grid">
                {dukeShowcase.showcase_skins.map(skinId => (
                  <div key={skinId} className="showcase-skin-card" data-testid={`showcase-skin-${skinId}`}>
                    <div className="showcase-skin-images">
                      <img src={getDukeImageUrl(skinId, 'instant_king')} alt={skinId} className="showcase-img" onError={(e) => { e.target.style.display='none'; }} />
                      <img src={getDukeImageUrl(skinId, 'fight_to_be_king')} alt={skinId} className="showcase-img" onError={(e) => { e.target.style.display='none'; }} />
                    </div>
                    <span className="showcase-skin-name">{skinId}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="showcase-empty">No featured skins yet. Click Edit to showcase your duke collection!</p>
            ))}
            {showcaseEditing && (
              <div className="showcase-picker" data-testid="showcase-picker">
                <p className="showcase-picker-hint">Select up to 6 skins to feature ({showcaseSelected.length}/6)</p>
                <div className="showcase-picker-grid">
                  {dukeShowcase.owned_duke_skins.map(skinId => (
                    <div
                      key={skinId}
                      className={`showcase-picker-item ${showcaseSelected.includes(skinId) ? 'selected' : ''}`}
                      onClick={() => toggleShowcaseSkin(skinId)}
                      data-testid={`showcase-pick-${skinId}`}
                    >
                      <div className="showcase-picker-imgs">
                        <img src={getDukeImageUrl(skinId, 'instant_king')} alt={skinId} className="showcase-pick-img" onError={(e) => { e.target.style.display='none'; }} />
                      </div>
                      <span className="showcase-picker-name">{skinId}</span>
                      {showcaseSelected.includes(skinId) && <div className="showcase-check">&#10003;</div>}
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="showcase-stat">
              <span>Duke skins owned: <strong>{dukeShowcase.owned_count || 0}</strong></span>
            </div>
          </div>
        )}


        {/* Gift Received Inbox */}
        {!isGuest && user && (
          <div className="gift-inbox-section" data-testid="gift-inbox-section">
            <div className="gift-inbox-header">
              <h2><i className="fa-solid fa-gift" style={{ marginRight: 8 }}></i> Gift Inbox</h2>
              {giftInbox.length > 0 && (
                <span className="gift-inbox-count" data-testid="gift-inbox-count">{giftInbox.length}</span>
              )}
              <button className="gift-inbox-toggle" onClick={() => setShowGiftInbox(!showGiftInbox)} data-testid="gift-inbox-toggle">
                {showGiftInbox ? 'Hide' : 'View'}
              </button>
            </div>
            {!showGiftInbox && giftInbox.length === 0 && (
              <p className="gift-inbox-empty">No gifts received yet.</p>
            )}
            {showGiftInbox && (
              <div className="gift-inbox-list" data-testid="gift-inbox-list">
                {giftInbox.length === 0 ? (
                  <p className="gift-inbox-empty">No gifts received yet. Ask a friend to gift you a Duke Set!</p>
                ) : (
                  giftInbox.map((gift) => (
                    <div key={gift.gift_id || gift.sender_name + gift.created_at} className="gift-inbox-item" data-testid={`gift-item-${gift.gift_id}`}>
                      <div className="gift-inbox-icon">
                        {gift.type === 'duke_set_gift' ? '🛡' : '🎁'}
                      </div>
                      <div className="gift-inbox-details">
                        <div className="gift-inbox-from">
                          <strong>{gift.sender_name || 'Someone'}</strong> gifted you
                          <span className="gift-inbox-set-name"> {gift.set_name || gift.skin_name || 'a skin'}</span>
                        </div>
                        {gift.message && (
                          <p className="gift-inbox-message">"{gift.message}"</p>
                        )}
                        <span className="gift-inbox-date">
                          {gift.created_at ? new Date(gift.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                        </span>
                      </div>
                      <div className={`gift-inbox-status ${gift.status}`}>
                        {gift.status === 'claimed' ? 'Claimed' : gift.status === 'pending' ? 'Pending' : gift.status}
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        )}



        {/* Purchase History Section */}
        {!isGuest && user && (
          <div className="purchase-history-section">
            <h2><i className="fa-solid fa-receipt" style={{ marginRight: 8 }}></i> Purchase History</h2>
            <p className="purchase-history-description">View your past purchases, subscriptions, and invoices.</p>
            <button
              className="view-purchase-history-btn"
              data-testid="view-purchase-history-btn"
              onClick={() => setShowPurchaseHistory(true)}
            >
              <i className="fa-solid fa-clock-rotate-left"></i> View Purchase History
            </button>
          </div>
        )}

        {showPurchaseHistory && (
          <PurchaseHistory onClose={() => setShowPurchaseHistory(false)} />
        )}

        {/* Notification Preferences */}
        {!isGuest && user && (
          <div className="notification-prefs-section" data-testid="notification-prefs-section">
            <h2><i className="fa-solid fa-bell" style={{ marginRight: 8, color: '#c9a227' }}></i> Notification Preferences</h2>
            <div className="notif-toggle-row">
              <div className="notif-toggle-info">
                <span className="notif-toggle-label">Trial Expiry Reminders</span>
                <span className="notif-toggle-desc">Get notified 24h before your trial ends and when it expires</span>
              </div>
              <label className="notif-switch" data-testid="trial-notif-toggle">
                <input
                  type="checkbox"
                  checked={trialNotificationsEnabled}
                  onChange={(e) => {
                    const newValue = e.target.checked;
                    setTrialNotificationsEnabled(newValue);
                    localStorage.setItem('trialNotificationsEnabled', JSON.stringify(newValue));
                  }}
                />
                <span className="notif-slider"></span>
              </label>
            </div>
          </div>
        )}

        {/* Legal Agreements Section */}
        {!isGuest && user && (
          <div className="legal-agreements-section">
            <h2>📜 {t('terms.legalAgreements', 'Legal Agreements')}</h2>
            <p className="legal-description">
              {t('terms.termsHistory', 'View your Terms of Service acceptance history and the exact terms you agreed to.')}
            </p>
            
            {termsHistory.length > 0 ? (
              <div className="terms-history-list">
                {termsHistory.map((agreement) => (
                  <div key={`terms-${agreement.terms_version}-${agreement.agreed_at}`} className="terms-agreement-card">
                    <div className="agreement-info">
                      <div className="agreement-version">
                        <span className="version-label">{t('terms.version', 'Version')}</span>
                        <span className="version-value">{agreement.terms_version}</span>
                      </div>
                      <div className="agreement-date">
                        <span className="date-label">{t('terms.acceptedOn', 'Accepted on')}</span>
                        <span className="date-value">
                          {new Date(agreement.agreed_at).toLocaleDateString(undefined, {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      </div>
                      {agreement.is_reacceptance && (
                        <span className="reacceptance-badge">Re-accepted</span>
                      )}
                    </div>
                    <button 
                      className="view-terms-btn"
                      onClick={() => viewAcceptedTerms(agreement.terms_version)}
                      disabled={loadingTerms}
                    >
                      {loadingTerms ? '...' : t('terms.viewTermsText', 'View Terms')}
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="no-history">{t('terms.noHistory', 'No terms acceptance history found.')}</p>
            )}
          </div>
        )}

        {/* Terms Text Modal */}
        {showTermsModal && selectedTermsText && (
          <div className="terms-text-modal-overlay" onClick={() => setShowTermsModal(false)}>
            <div className="terms-text-modal" onClick={(e) => e.stopPropagation()}>
              <div className="terms-text-header">
                <h3>📜 Terms of Service - Version {selectedTermsVersion}</h3>
                <button className="close-terms-btn" onClick={() => setShowTermsModal(false)}>×</button>
              </div>
              <div className="terms-text-content">
                <pre>{selectedTermsText}</pre>
              </div>
              <div className="terms-text-footer">
                <button className="close-btn" onClick={() => setShowTermsModal(false)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Account Section */}
        {!isGuest && user && !loading && (
          <div className="delete-account-section">
            <h3>⚠️ Danger Zone</h3>
            <p>Once you delete your account, there is no going back. All your data, stats, and progress will be permanently deleted.</p>
            <button 
              className="delete-account-btn"
              onClick={() => {
                if (window.confirm('Are you sure you want to delete your account? This action cannot be undone!')) {
                  handleDeleteAccount();
                }
              }}
            >
              Delete My Account
            </button>
          </div>
        )}
        
        {/* Build Version Display */}
        <div className="build-version">
          <small style={{ color: '#888', fontSize: '12px' }}>Build v1.0.8 - Syntax Fix Final</small>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
